import { capabilityDomains } from "@zylarity/contracts";

// Fictional, reviewed demonstration levels use the same axes and scale as live reports.
const sampleLevels = [
  {
    candidate: 4,
    requirement: 3,
    evidence: "Led industrial control system changes with plant engineers and safety leads.",
  },
  {
    candidate: 4,
    requirement: 4,
    evidence:
      "Led zones and conduits design across three plants with staged cutovers and tested rollback.",
  },
  {
    candidate: 3,
    requirement: 3,
    evidence:
      "Implemented privileged remote access and least-privilege controls for engineering workstations.",
  },
  {
    candidate: 4,
    requirement: 3,
    evidence:
      "Led passive discovery of 1,240 assets and vulnerability remediation planning with plant engineers.",
  },
  {
    candidate: 3,
    requirement: 3,
    evidence:
      "Independently built and tuned detections for unusual engineering access and controller changes.",
  },
  {
    candidate: 2,
    requirement: 3,
    evidence:
      "Alex’s experience: Assisted (level 2). Role expectation: Independent (level 3). The example describes assisted response work. The interview should clarify which decisions Alex has been responsible for and what support was needed.",
  },
  {
    candidate: 4,
    requirement: 4,
    evidence:
      "Led IEC 62443 zone risk reviews and translated the findings into approved target architecture.",
  },
  {
    candidate: 4,
    requirement: 4,
    evidence:
      "Led security program delivery and cross-functional risk reviews across three plants.",
  },
] as const;
export const sampleCapabilities = capabilityDomains.map((domain, index) => ({
  label: domain.label,
  shortLabel: domain.shortLabel,
  ...sampleLevels[index]!,
}));

export const sampleComparison = {
  same: sampleCapabilities.filter((item) => item.candidate === item.requirement),
  broader: sampleCapabilities.filter((item) => item.candidate > item.requirement),
  followUp: sampleCapabilities.filter((item) => item.candidate < item.requirement),
};
const countWords = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight"];
export const sampleSameCount =
  countWords[sampleComparison.same.length] ?? String(sampleComparison.same.length);
export const sampleComparisonSummary = `${sampleSameCount} areas show the same responsibility level as the role. Industrial systems and asset management show broader responsibility. Alex has supported incident response exercises. The role requires independent response work, so the interview should clarify which decisions Alex could make within the role’s authority.`;

export const candidateProjects = [
  {
    title: "Three-plant segmentation program",
    context:
      "Manufacturing networks with shared engineering access and tightly scheduled maintenance windows.",
    result:
      "Delivered a phased zones and conduits design, remote access controls and plant-approved cutover plans.",
    evidence:
      "Architecture walkthrough, redacted network diagrams and a maintenance-window rollback scenario.",
  },
  {
    title: "Asset visibility and detection rollout",
    context:
      "Three plants needed a shared inventory without active scanning of production controllers.",
    result:
      "Reconciled 1,240 assets, assigned ownership and deployed detections for engineering access and controller changes.",
    evidence:
      "Inventory reconciliation exercise, sample detection logic and an alert triage walkthrough.",
  },
  {
    title: "OT incident readiness exercise",
    context:
      "A simulated engineering workstation compromise required coordination between plant staff and the security team.",
    result:
      "Supported evidence collection and recovery planning while the incident lead owned containment decisions.",
    evidence:
      "Tabletop debrief and response checklist; independent response decisions still need to be explored.",
  },
] as const;

export const roleOutcomes = [
  {
    period: "30 days",
    title: "Understand the plants",
    description:
      "Meet operations and engineering leads at all three plants. Baseline asset visibility, remote access and the highest-priority production risks.",
  },
  {
    period: "60 days",
    title: "Agree the delivery plan",
    description:
      "Secure plant approval for a prioritized segmentation roadmap, detection backlog and maintenance-window implementation plan.",
  },
  {
    period: "90 days",
    title: "Prove the approach",
    description:
      "Complete the first segmentation change, validate its rollback plan and independently lead an OT incident response exercise.",
  },
] as const;

export const matchActions = [
  {
    title: "Explore incident response responsibilities",
    description:
      "Use a plant incident scenario to explore which response decisions Alex could make, when operations would need to be involved and what Alex would escalate.",
  },
  {
    title: "Explore the architecture in detail",
    description:
      "Walk through a three-plant segmentation design, including emergency access, production constraints and rollback decisions. Ask Alex to explain the choices and tradeoffs.",
  },
  {
    title: "Confirm the practical fit",
    description:
      "Confirm the Ohio hybrid schedule and up to 25% plant travel. Alex's $150k to $165k base target sits within the role's $150k to $175k range.",
  },
] as const;

export const roleCoverageFields = [
  "Role mandate",
  "Plant environment",
  "Reporting line",
  "Required capabilities",
  "First 90-day outcomes",
  "Compensation range",
  "Location and hybrid schedule",
  "Travel expectations",
  "Assessment approach",
  "Interview process",
] as const;

export const candidateCoverageFields = [
  "Experience and progression",
  "Project delivery",
  "Capability evidence",
  "Leadership scope",
  "Location and work preference",
  "Travel preference",
  "Compensation expectations",
  "Assessment follow-up",
] as const;
